"use client";

import { useEffect, useRef, type ReactNode } from "react";

/**
 * An `<h1>` that takes focus once, on mount.
 *
 * Failure surfaces are the one place where hijacking focus is the *correct*
 * call rather than a nuisance: the user's mental model just broke, and the
 * two things they need are (a) to hear what happened and (b) for the very
 * next Tab to land on a recovery action instead of walking the whole page
 * chrome. Next.js client-side navigations do not reliably move focus into a
 * replaced segment, so a boundary swapped in under an existing layout would
 * otherwise leave focus wherever the user last clicked.
 *
 * `preventScroll` because the browser has already positioned the viewport;
 * an extra scroll-into-view here reads as a jolt.
 */
export function AutoFocusHeading({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);

  return (
    // tabIndex -1 makes the heading programmatically focusable without
    // inserting it into the Tab order.
    <h1 ref={ref} tabIndex={-1} className={className}>
      {children}
    </h1>
  );
}
